import type { GalleryCategory } from '@bannermatic/core';

/** Texts of the marketing pages, one object per language (content/en.ts, content/uk.ts, …). */

export const USE_CASE_IDS = [
  'product-cards',
  'catalog-ads',
  'ad-creatives',
  'social-media',
  'google-sheets',
  'image-api',
  'og-images',
  'certificates',
] as const;
export const COMPARE_IDS = [
  'manual-vs-automated',
  'design-to-production',
  'image-api-checklist',
] as const;

export type UseCaseId = (typeof USE_CASE_IDS)[number];
export type CompareId = (typeof COMPARE_IDS)[number];
export type MarketingPageId = UseCaseId | CompareId;
export type HubId = 'use-cases' | 'compare';

export interface Item {
  title: string;
  text: string;
}

export interface PageCopy {
  meta: { title: string; description: string };
  /** Short name for menus, hub cards and breadcrumbs. */
  label: string;
  /** One sentence for hub cards and "related" links. */
  summary: string;
  eyebrow: string;
  h1: string;
  lead: string;
  pains?: { title: string; items: string[] };
  steps: { title: string; items: Item[] };
  features: { title: string; items: Item[] };
  /** Table with three columns, e.g. [criterion, by hand, with Bannermatic]. */
  table?: { title: string; head: [string, string, string]; rows: Array<[string, string, string]> };
  /** Heading and text above the code sample (pages with code in shared.ts). */
  code?: Item;
  /** Data rows for the "one template — many banners" picture, in the order of its fields. */
  samples?: string[][];
  faq: Array<{ q: string; a: string }>;
  cta: Item;
}

export interface HubCopy {
  meta: { title: string; description: string };
  label: string;
  eyebrow: string;
  h1: string;
  lead: string;
}

export interface MarketingUi {
  navUseCases: string;
  navCompare: string;
  navTemplates: string;
  navSizes: string;
  navPricing: string;
  navDocs: string;
  signIn: string;
  startFree: string;
  openApp: string;
  home: string;
  noCard: string;
  seeApi: string;
  painsLabel: string;
  faqTitle: string;
  relatedTitle: string;
  learnMore: string;
  rowsCaption: string;
  sizesCaption: string;
  footerUseCases: string;
  footerCompare: string;
  footerProduct: string;
  privacy: string;
  terms: string;
}

export const CONTACT_TOPICS = ['support', 'billing', 'sales', 'other'] as const;
export type ContactTopic = (typeof CONTACT_TOPICS)[number];

/** The contact page: support email and the feedback form. */
export interface ContactCopy {
  meta: { title: string; description: string };
  label: string;
  eyebrow: string;
  h1: string;
  lead: string;
  emailTitle: string;
  emailText: string;
  docsTitle: string;
  docsText: string;
  form: {
    title: string;
    name: string;
    email: string;
    topic: string;
    topics: Record<ContactTopic, string>;
    message: string;
    messagePlaceholder: string;
    send: string;
    sending: string;
    successTitle: string;
    successText: string;
    /** Shown when sending failed; {email} is the support address. */
    error: string;
    privacy: string;
  };
}

/** Texts of the template gallery pages. In templates, {name} is the template, {category} its category. */
export interface GalleryCopy {
  hub: {
    meta: { title: string; description: string };
    label: string;
    eyebrow: string;
    h1: string;
    lead: string;
  };
  /** "{n} templates" */
  count: string;
  categories: Record<
    GalleryCategory,
    { name: string; h1: string; lead: string; metaTitle: string; metaDescription: string }
  >;
  template: {
    metaTitle: string;
    metaDescription: string;
    h1: string;
    lead: string;
    use: string;
    useHint: string;
    sizesTitle: string;
    sizesText: string;
    fieldsTitle: string;
    fieldsText: string;
    fieldHead: [string, string, string];
    types: Record<'text' | 'image' | 'qr', string>;
    csvTitle: string;
    csvText: string;
    apiTitle: string;
    apiText: string;
    moreTitle: string;
    allTemplates: string;
  };
}

/** Texts of the size pages. In templates, {name} is the format, {w}×{h} its size, {ratio} its aspect. */
export interface SizesCopy {
  hub: {
    meta: { title: string; description: string };
    label: string;
    eyebrow: string;
    h1: string;
    lead: string;
  };
  tableHead: [string, string, string];
  groups: Record<'social' | 'ads' | 'web' | 'video' | 'print', string>;
  orientations: Record<'square' | 'portrait' | 'landscape', string>;
  page: {
    metaTitle: string;
    metaDescription: string;
    h1: string;
    factsTitle: string;
    facts: { size: string; ratio: string; orientation: string; group: string };
    previewTitle: string;
    previewText: string;
    ctaTitle: string;
    ctaText: string;
    otherTitle: string;
    faq: Array<{ q: string; a: string }>;
  };
  presets: Record<string, { name: string; text: string }>;
}

export interface MarketingCopy {
  ui: MarketingUi;
  contact: ContactCopy;
  gallery: GalleryCopy;
  sizes: SizesCopy;
  hubs: Record<HubId, HubCopy>;
  pages: Record<MarketingPageId, PageCopy>;
}
